// 数据导出工具
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'

/**
 * 导出为JSON文件
 * @param {Object} data - 要导出的数据
 * @param {string} filename - 文件名
 */
export function exportToJSON(data, filename = 'rpi-result.json') {
  try {
    const jsonStr = JSON.stringify(data, null, 2)
    const blob = new Blob([jsonStr], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
    return true
  } catch (error) {
    console.error('导出JSON失败:', error)
    throw new Error('导出JSON失败')
  }
}

/**
 * 导出为CSV文件
 * @param {Object} result - 评估结果
 * @param {string} filename - 文件名
 */
export function exportToCSV(result, filename = 'rpi-result.csv') {
  try {
    // 构建CSV内容
    const rows = []
    
    // 标题行
    rows.push(['项目', '值'])
    rows.push(['RPI指数', result.rpiScore])
    rows.push(['等级', result.rpiLevel.level])
    rows.push(['依恋类型', result.attachmentType.name])
    rows.push(['评估类型', result.assessmentType === 'self' ? '给自己测' : '为恋人测'])
    rows.push(['评估时间', new Date(result.timestamp).toLocaleString('zh-CN')])
    rows.push([]) // 空行
    
    // 维度分数
    rows.push(['维度名称', '原始分', 'T分数', 'Z分数'])
    result.dimensionResults.forEach(dimension => {
      rows.push([
        dimension.name,
        dimension.rawScore,
        dimension.tScore,
        dimension.zScore
      ])
    })
    
    // 转换为CSV字符串
    const csvContent = rows.map(row => 
      row.map(cell => {
        // 处理包含逗号或引号的单元格
        const cellStr = String(cell)
        if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
          return `"${cellStr.replace(/"/g, '""')}"`
        }
        return cellStr
      }).join(',')
    ).join('\n')
    
    // 添加BOM以支持Excel正确显示中文
    const BOM = '\uFEFF'
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
    return true
  } catch (error) {
    console.error('导出CSV失败:', error)
    throw new Error('导出CSV失败')
  }
}

/**
 * 导出完整数据包（包含所有评估数据）
 * @param {string} filename - 文件名
 * @param {string} assessmentType - 测评类型 ('self' 或 'partner')
 */
export function exportFullData(filename = 'rpi-full-data.json', assessmentType = 'self') {
  try {
    const storageKey = `rpi_answers_${assessmentType}`
    const data = {
      consent: JSON.parse(localStorage.getItem('rpi_consent') || 'null'),
      basicInfo: JSON.parse(localStorage.getItem('rpi_basic_info') || 'null'),
      answers: JSON.parse(localStorage.getItem(storageKey) || 'null'),
      result: JSON.parse(localStorage.getItem('rpi_result') || 'null'),
      exportTime: new Date().toISOString()
    }
    
    return exportToJSON(data, filename)
  } catch (error) {
    console.error('导出完整数据失败:', error)
    throw new Error('导出完整数据失败')
  }
}

/**
 * 生成文本报告
 * @param {Object} result - 评估结果
 * @returns {string} 文本报告
 */
export function generateTextReport(result) {
  const lines = []
  
  lines.push('=' .repeat(50))
  lines.push('恋爱占有欲指数(RPI)评估报告')
  lines.push('=' .repeat(50))
  lines.push('')
  
  lines.push(`评估时间：${new Date(result.timestamp).toLocaleString('zh-CN')}`)
  lines.push(`评估类型：${result.assessmentType === 'self' ? '给自己测' : '为恋人测'}`)
  lines.push('')
  
  lines.push('-'.repeat(50))
  lines.push('总体结果')
  lines.push('-'.repeat(50))
  lines.push(`RPI指数：${result.rpiScore}`)
  lines.push(`等级：${result.rpiLevel.level}`)
  lines.push(`人格类型：${result.rpiLevel.type}`)
  lines.push(`依恋类型：${result.attachmentType.name}`)
  lines.push('')
  
  lines.push('-'.repeat(50))
  lines.push('四维度分析')
  lines.push('-'.repeat(50))
  result.dimensionResults.forEach(dimension => {
    lines.push(`${dimension.name}`)
    lines.push(`  原始分：${dimension.rawScore}`)
    lines.push(`  T分数：${dimension.tScore}`)
    lines.push(`  Z分数：${dimension.zScore}`)
    lines.push(`  说明：${dimension.description}`)
    lines.push('')
  })
  
  lines.push('=' .repeat(50))
  lines.push('报告结束')
  lines.push('=' .repeat(50))
  
  return lines.join('\n')
}

/**
 * 导出文本报告
 * @param {Object} result - 评估结果
 * @param {string} filename - 文件名
 */
export function exportTextReport(result, filename = 'rpi-report.txt') {
  try {
    const report = generateTextReport(result)
    const blob = new Blob([report], { type: 'text/plain;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
    return true
  } catch (error) {
    console.error('导出文本报告失败:', error)
    throw new Error('导出文本报告失败')
  }
}

/**
 * 导出为图片
 * @param {HTMLElement} element - 要截图的元素
 * @param {string} filename - 文件名
 */
export async function exportToImage(element, filename = 'rpi-result.png') {
  try {
    // 隐藏不需要导出的元素
    const noExportElements = element.querySelectorAll('.no-print, .no-export')
    noExportElements.forEach(el => {
      el.style.display = 'none'
    })

    // 使用 html2canvas 截图
    const canvas = await html2canvas(element, {
      backgroundColor: '#ffffff',
      scale: 2, // 提高清晰度
      useCORS: true,
      logging: false,
      windowWidth: element.scrollWidth,
      windowHeight: element.scrollHeight
    })

    // 恢复隐藏的元素
    noExportElements.forEach(el => {
      el.style.display = ''
    })

    // 转换为 blob 并下载
    canvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    }, 'image/png')

    return true
  } catch (error) {
    console.error('导出图片失败:', error)
    throw new Error('导出图片失败')
  }
}

/**
 * 导出为PDF
 * @param {HTMLElement} element - 要导出的元素
 * @param {string} filename - 文件名
 */
export async function exportToPDF(element, filename = 'rpi-result.pdf') {
  try {
    // 隐藏不需要导出的元素
    const noExportElements = element.querySelectorAll('.no-print, .no-export')
    noExportElements.forEach(el => {
      el.style.display = 'none'
    })

    // 使用 html2canvas 截图
    const canvas = await html2canvas(element, {
      backgroundColor: '#ffffff',
      scale: 2,
      useCORS: true,
      logging: false,
      windowWidth: element.scrollWidth,
      windowHeight: element.scrollHeight
    })

    // 恢复隐藏的元素
    noExportElements.forEach(el => {
      el.style.display = ''
    })

    // 创建 PDF
    const imgData = canvas.toDataURL('image/png')
    const pdf = new jsPDF({
      orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
      unit: 'mm',
      format: 'a4'
    })

    // 计算图片在 PDF 中的尺寸
    const pdfWidth = pdf.internal.pageSize.getWidth()
    const pdfHeight = pdf.internal.pageSize.getHeight()
    const imgWidth = canvas.width
    const imgHeight = canvas.height
    const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight)
    const imgX = (pdfWidth - imgWidth * ratio) / 2
    const imgY = 10

    // 添加图片到 PDF
    pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio)

    // 保存 PDF
    pdf.save(filename)
    return true
  } catch (error) {
    console.error('导出PDF失败:', error)
    throw new Error('导出PDF失败')
  }
}

/**
 * 打印结果页面
 */
export function printResult() {
  try {
    window.print()
    return true
  } catch (error) {
    console.error('打印失败:', error)
    throw new Error('打印失败')
  }
}

